import { brandclub_colors, CustomThemeProvider } from "@brandclub/common-ui";
import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
  ThemeOptions,
} from "@mui/material";
import React, { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import BCCheckoutBar, {
  BCCheckoutBarProps,
} from "./modules/CheckoutBar/BCCheckoutBar";
import { filterEnabledOverrides } from "./utils/style";
import _isEmpty from "lodash/isEmpty";

import { createPortal } from "react-dom";
import BuyBoxDialog from "./components/dialog/BuyboxDialog";
import ShareProductDialog from "./components/dialog/ShareDialog";
import InpageAddToBagButton from "./modules/AddToBagButton/InpageAddToBagButton";
import BCBagButton from "./modules/BagButton/BCBagButton";
import MembershipButton from "./modules/MembershipButton/MembershipButton";
import BCProfileButton from "./modules/ProfileButton/BCProfileButton";
import GetAppBanners from "./modules/GetAppBanners";
import { useQuery } from "@tanstack/react-query";
import { appThemeQueryOptions } from "./api/cache/appThemeQueryOptions";
import { StyleDataType } from "./theme";
import { useUserProfile } from "./api/cache/userProfileQueryOptions";
// import { AutoShowMembershipDialog } from "./components/dialog/MembershipDialog/AutoShowMembershipDialog";

const InAppCheckout = () => {
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useUserProfile();
  const isLoggedIn = !isUserProfileLoading && !!userProfile;
  const bagButtonElement = document.querySelector(".bc-header-cart-icon");
  const bagButtonElementMobile = document.querySelector(
    ".bc-header-cart-icon-mobile"
  );

  const membershipButtonElement = document.querySelector(
    ".bc-header-membership-icon"
  ) as HTMLDivElement | null;

  const membershipButtonElementMobile = document.querySelector(
    ".bc-header-membership-icon-mobile"
  ) as HTMLDivElement | null;

  const profileButtonElement = document.querySelector(
    ".bc-header-profile-icon"
  ) as HTMLDivElement | null;

  const profileButtonElementMobile = document.querySelector(
    ".bc-header-profile-icon-mobile"
  ) as HTMLDivElement | null;

  const inpageAddToBagButtonElement = document.querySelector(
    ".bc-inpage-addtobag-button"
  ) as HTMLDivElement | null; //<div class="bc-inpage-addtobag-button"></div> in main-product.liquid

  const inpageAddToBagButtonElementMobile = document.querySelector(
    ".bc-inpage-addtobag-button-mobile"
  ) as HTMLDivElement | null;

  useEffect(() => {
    // Hide membership elements when user is logged in to preserve layout
    if (membershipButtonElementMobile) {
      if (isLoggedIn) {
        membershipButtonElementMobile.style.display = "none";
      } else {
        membershipButtonElementMobile.style.removeProperty("display");
      }
    }

    if (membershipButtonElement) {
      if (isLoggedIn) {
        membershipButtonElement.style.display = "none";
      } else {
        membershipButtonElement.style.removeProperty("display");
      }
    }
  }, [isLoggedIn]);

  return (
    <>
      {bagButtonElement
        ? createPortal(<BCBagButton />, bagButtonElement)
        : null}

      {bagButtonElementMobile
        ? createPortal(<BCBagButton mobile={true} />, bagButtonElementMobile)
        : null}

      {membershipButtonElement
        ? createPortal(<MembershipButton />, membershipButtonElement)
        : null}

      {membershipButtonElementMobile
        ? createPortal(
            <MembershipButton mobile={true} />,
            membershipButtonElementMobile
          )
        : null}

      {profileButtonElement
        ? createPortal(<BCProfileButton />, profileButtonElement)
        : null}

      {profileButtonElementMobile
        ? createPortal(
            <BCProfileButton mobile={true} />,
            profileButtonElementMobile
          )
        : null}
      {inpageAddToBagButtonElement
        ? createPortal(<InpageAddToBagButton />, inpageAddToBagButtonElement)
        : null}

      {inpageAddToBagButtonElementMobile
        ? createPortal(
            <InpageAddToBagButton mobile={true} />,
            inpageAddToBagButtonElementMobile
          )
        : null}
    </>
  );
};

const buildTheme = (
  styleData: Partial<StyleDataType> | undefined
): ThemeOptions | undefined => {
  if (!styleData || !styleData.primary || !styleData.secondary) {
    return undefined;
  }

  return {
    palette: {
      primary: {
        main: styleData.primary,
      },
      secondary: {
        main: styleData.secondary,
      },
    },
    dtc: true,
    baseFontSize: styleData.barFonts?.baseFontSize || 20,
    fontWeight: styleData.barFonts?.fontWeight,
    globalButtonRadius: styleData.globalButtonRadius,
    customRewardPillColor: styleData.customRewardPillColor,
    componentStyleOverrides:
      filterEnabledOverrides(styleData.componentStyleOverrides) || {},
    getAppBanner: styleData.getAppBanner,
  };
};

const CheckoutShopApp: React.FC<BCCheckoutBarProps> = () => {
  const { data: styleData } = useQuery(appThemeQueryOptions());

  const customTheme = buildTheme(styleData);

  let theme = createTheme({
    palette: {
      primary: {
        main: brandclub_colors.mainSeriesColor,
      },
    },
    typography: {
      fontFamily: [
        styleData?.barFonts?.fontFamily || "Poppins",
        "Poppins",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
    },
  });

  useEffect(() => {
    const link = document.createElement("link");
    const checkoutBar = document.getElementById("BCCheckoutBar");
    if (styleData?.barFonts?.fontFamily) {
      if (styleData.barFonts.fontFamilyLink) {
        link.rel = "stylesheet";
        link.href = styleData.barFonts.fontFamilyLink;
        document.head.appendChild(link);
      }
      if (checkoutBar) {
        checkoutBar.style.fontFamily = styleData.barFonts.fontFamily || "Arial";
      }
    }
  }, [styleData]);

  // Default to inpage checkout when style data is missing to prevent checkout bar flashing during load
  const activeCheckoutBar =
    styleData && !_isEmpty(styleData) && !styleData.activeInpageCheckout;
  return (
    <CookiesProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CustomThemeProvider themeOptions={customTheme}>
            <BuyBoxDialog />
            {/* <AutoShowMembershipDialog /> */}
            <ShareProductDialog />
            <GetAppBanners />
            {activeCheckoutBar ? <BCCheckoutBar /> : <InAppCheckout />}
          </CustomThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </CookiesProvider>
  );
};

export default CheckoutShopApp;
